import { useLayoutEffect } from 'react';
import { useSnapshot } from 'valtio';

const DUMMY_SYMBOL = Symbol();

/**
 * useProxy
 *
 * Takes a proxy and returns a new proxy which you can use in both react render
 * and in callbacks. The root reference is replaced on every render, but the
 * keys (and subkeys) below it are stable until they're intentionally mutated.
 * For the best ergonomics, you can export a custom hook from your store, so you
 * don't have to figure out a separate name for the hook reference. E.g.:
 *
 * export const store = proxy(initialState)
 * export const useStore = () => useProxy(store)
 * // in the component file:
 * function Cmp() {
 *   const store = useStore()
 *   return <button onClick={() => {store.count++}}>{store.count}</button>
 * }
 *
 * @param proxy
 * @param options
 * @returns A new proxy which you can use in the render as well as in callbacks.
 */
export function useProxy<T extends object>(
  proxy: T,
  options?: NonNullable<Parameters<typeof useSnapshot>[1]>,
): T {
  const snapshot = useSnapshot(proxy, options) as T;

  // touch dummy prop so that it doesn't trigger re-renders when no props are touched.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  (snapshot as any)[DUMMY_SYMBOL];

  let isRendering = true;
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    isRendering = false;
  });

  return new Proxy(proxy, {
    get(target, prop) {
      const value = Reflect.get((isRendering ? snapshot : target) as object, prop);

      // Wrap all function. This is necessary in case one function gets passed as a prop to a child component.
      if (typeof value === 'function') {
        if ((import.meta as any).env.DEV && isArrowFunction(value)) {
          console.error(
            `Store methods should not be arrow functions. Please use regular function declarations. Check the method: ${String(
              prop,
            )}`,
          );
        }

        return new Proxy(value, {
          apply(fnTarget, _, argsList) {
            return Reflect.apply(fnTarget, isRendering ? snapshot : target, argsList);
          },
        });
      }

      // For non-function properties, just return the value
      return value;
    },
  });
}
/** `function` / `async function` declarations (not arrow). */
const isFunctionDeclarationRegex = /^\s*(?:async\s+)?function\b/;
/** Class/object method syntax: `methodName(args) {` — `)` is followed by `{`, not `=>`. */
const isMethodSyntaxRegex = /^\s*(?:async\s+)?[\w$][\w$.]*\s*\([^)]*\)\s*\{/;
/** Root arrow only: `() =>`, `(a, b) =>`, `async () =>`, `param =>`. */
const isRootArrowFunctionRegex = /^\s*(?:async\s+)?(?:\([^)]*\)|[\w$]+)\s*=>/;

export function isArrowFunction(fn: (...args: any[]) => any): boolean {
  const fnStr = fn.toString();
  if (fnStr.length === 0) {
    return false;
  }
  if (isFunctionDeclarationRegex.test(fnStr) || isMethodSyntaxRegex.test(fnStr)) {
    return false;
  }
  return isRootArrowFunctionRegex.test(fnStr);
}
